/**
 * @packageDocumentation
 *
 * Similar to [AbortSignal.any](https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/any_static)
 * except the returned promise has a `.clear` method that removes all event
 * listeners added to passed signals preventing memory leaks.
 *
 * At the time of writing at least, `AbortSignal.any` leaks memory in Node.js
 * and Deno environments:
 *
 * - https://github.com/nodejs/node/issues/54614
 * - https://github.com/denoland/deno/issues/24842
 *
 * @example
 *
 * ```js
 * import { anySignal } from 'any-signal'
 *
 * const userController = new AbortController()
 *
 * // Abort after 1 second
 * const timeoutSignal = AbortSignal.timeout(1000)
 *
 * const combinedSignal = anySignal([userController.signal, timeoutSignal])
 * combinedSignal.addEventListener('abort', () => console.log('Abort!'))
 *
 * try {
 *   // The user or the timeout can now abort the action
 *   await performSomeAction({ signal: combinedSignal })
 * } finally {
 *   // Clear will clean up internal event handlers
 *   combinedSignal.clear()
 * }
 * ```
 */

export interface ClearableSignal extends AbortSignal {
  clear(): void
}

/**
 * Takes an array of AbortSignals and returns a single signal.
 * If any signals are aborted, the returned signal will be aborted.
 */
export function anySignal (signals: Array<AbortSignal | undefined | null>): ClearableSignal {
  const controller = new globalThis.AbortController()

  function onAbort (): void {
    const reason = signals
      .filter(s => s?.aborted === true)
      .map(s => s?.reason)
      .pop()

    controller.abort(reason)

    for (const signal of signals) {
      if (signal?.removeEventListener != null) {
        signal.removeEventListener('abort', onAbort)
      }
    }
  }

  for (const signal of signals) {
    if (signal?.aborted === true) {
      onAbort()
      break
    }

    if (signal?.addEventListener != null) {
      signal.addEventListener('abort', onAbort)
    }
  }

  function clear (): void {
    for (const signal of signals) {
      if (signal?.removeEventListener != null) {
        signal.removeEventListener('abort', onAbort)
      }
    }
  }

  const signal = controller.signal as ClearableSignal
  signal.clear = clear

  return signal
}
